// What the QuickBooks card says when the OAuth callback redirects back with
// `?qb_auth=error&reason=…` (server/routes.ts, the /quickbooks/callback handler).

/**
 * QB-7a: a reconnect that chose a different QuickBooks company from the one this
 * organization was connected to is refused (#169). Customer, item and invoice links
 * belong to one company, so the tenant is told what happened and what to do. Neither
 * company id is ever shown — the redirect carries none, and nothing else is read.
 *
 * No "Disconnect first to switch" advice until QB-7b ships: switching companies today
 * reuses the old company's customer/item links in the new company's books.
 */
export const REALM_MISMATCH_MESSAGE =
  "QuickBooks was not connected: the company you picked is a different QuickBooks company " +
  "from the one this account was connected to before. Connect again and pick the original " +
  "company.";

export function quickbooksAuthErrorMessage(reason: string | null, description: string | null): string {
  if (reason === "realm_mismatch") return REALM_MISMATCH_MESSAGE;

  let message = "QuickBooks authentication failed.";
  if (reason) message += ` Reason: ${reason}.`;
  if (description && description !== "none") message += ` Details: ${decodeURIComponent(description)}`;
  return message;
}

/**
 * The page's query string once the card has read the callback's result: the callback's
 * own params go, everything else stays. `tab=admin` above all — Settings re-reads its
 * tab from the query string whenever it changes, and the card is only mounted while the
 * Admin tab is open, so clearing the whole query closed the tab and took the card's
 * message with it (QB-7a, re-opened 2026-10-03).
 */
export function withoutQuickbooksAuthParams(search: string): string {
  const params = new URLSearchParams(search);
  for (const key of ["qb_auth", "reason", "description"]) params.delete(key);
  const rest = params.toString();
  return rest ? `?${rest}` : "";
}
